/**
 * Measures a truss design drawing's members from the vector lines in its PDF, so the model can build the truss from
 * real coordinates rather than reading them off a picture of the drawing.
 *
 * Drawings such as MiTek's draw each member as an outline at a stated scale ("Scale = 1:63.0"). Each outline that is
 * long and about a board wide is taken as a member; the largest group of touching members is the truss.
 */

import { describeDrawingLoads, readDrawingLoads } from './drawingLoads';

type Matrix = readonly [number, number, number, number, number, number];
type Point = { readonly x: number; readonly y: number };

export interface DrawingText {
  readonly x: number;
  readonly y: number;
  readonly text: string;
  /** The text's height on the page, when the stream sets a font size. */
  readonly size?: number;
}

export interface DrawingPage {
  readonly paths: readonly (readonly Point[])[];
  readonly texts: readonly DrawingText[];
}

export interface MeasuredMember {
  readonly from: Point;
  readonly to: Point;
  readonly width: number;
  /** The drawing's name for the member, such as B2, when its label sits on it. */
  readonly label?: string;
  /**
   * The longer member this one lies against, face to face and parallel, such as the top chord a heel slider runs
   * along: its label, or "a chord" when it has none.
   */
  readonly against?: string;
}

/** A corner of the truss's outline, as create_truss takes it: `line` runs from this corner to the next. */
export interface MeasuredOutlinePoint extends Point {
  readonly line: 'Heel' | 'TopChord' | 'BottomChord';
  /** The dimensions this corner was snapped to, such as "7-0-0, 8-1-12"; absent when it's only measured. */
  readonly from?: string;
}

/** A dimension along a chord, with what the lines show there. */
export interface ChordDimension {
  readonly text: string;
  readonly x: number;
  /** What's drawn at it: "a corner", or the web that meets the chord there; `null` when nothing is. */
  readonly drawn: string | null;
}

/** The drawing's typed dimensions, matched to what its lines show. */
export interface DrawingDimensions {
  /** The running totals along the top and bottom of the truss, from its left end. */
  readonly topChord: readonly ChordDimension[];
  readonly bottomChord: readonly ChordDimension[];
  readonly heights: { readonly left: readonly string[]; readonly right: readonly string[] };
  /** Where the dimensions and the lines differ by more than the lines can be off. */
  readonly disagreements: readonly string[];
}

export interface MeasuredDrawing {
  readonly scale: number;
  readonly overallLength: number;
  readonly height: number;
  readonly members: readonly MeasuredMember[];
  readonly jointLabels: readonly DrawingText[];
  /** Along the chords' outer faces, clockwise from the bottom-left, without overhangs; `null` if it can't be traced. */
  readonly outline: readonly MeasuredOutlinePoint[] | null;
  readonly overhangs: { readonly left: number; readonly right: number } | null;
  /** `null` when the drawing has no dimensions to read near the truss. */
  readonly dimensions: DrawingDimensions | null;
}

const IDENTITY: Matrix = [1, 0, 0, 1, 0, 0];

function multiply([a, b, c, d, e, f]: Matrix, [a2, b2, c2, d2, e2, f2]: Matrix): Matrix {
  return [
    a * a2 + b * c2,
    a * b2 + b * d2,
    c * a2 + d * c2,
    c * b2 + d * d2,
    e * a2 + f * c2 + e2,
    e * b2 + f * d2 + f2,
  ];
}

function apply([a, b, c, d, e, f]: Matrix, x: number, y: number): Point {
  return { x: a * x + c * y + e, y: b * x + d * y + f };
}

const latin1 = new TextDecoder('latin1');

/**
 * Splits a content stream into operands and operators. Strings are kept with their parentheses, arrays as their
 * strings joined, since only text positions and contents matter here.
 */
function* tokenize(content: string): Generator<string> {
  let index = 0;
  while (index < content.length) {
    const char = content[index];
    if (/\s/.test(char)) {
      index++;
    } else if (char === '%') {
      while (index < content.length && content[index] !== '\n' && content[index] !== '\r') index++;
    } else if (char === '(') {
      let depth = 0;
      let text = '';
      for (index++; index < content.length; index++) {
        const next = content[index];
        if (next === '\\') {
          text += content[++index] ?? '';
        } else if (next === '(') {
          depth++;
          text += next;
        } else if (next === ')') {
          if (depth === 0) break;
          depth--;
          text += next;
        } else {
          text += next;
        }
      }
      index++;
      yield `(${text}`;
    } else if (char === '[' || char === ']' || char === '{' || char === '}') {
      index++;
      yield char;
    } else if (char === '<' && content[index + 1] !== '<') {
      const end = content.indexOf('>', index);
      index = end < 0 ? content.length : end + 1;
      yield '(';
    } else {
      const start = index;
      index++;
      while (index < content.length && !/[\s()[\]{}<>/%]/.test(content[index])) index++;
      yield content.slice(start, index);
    }
  }
}

/** Reads one page's content stream into its drawn paths and its text, in page coordinates. */
export function readContentStream(content: string): DrawingPage {
  const paths: Point[][] = [];
  const texts: DrawingText[] = [];

  let ctm: Matrix = IDENTITY;
  const stack: Matrix[] = [];
  let current: Point[][] = [];
  let textMatrix: Matrix = IDENTITY;
  let lineMatrix: Matrix = IDENTITY;
  let leading = 0;
  let fontSize = 0;
  let operands: string[] = [];
  let arrayText: string | null = null;

  const numbers = (count: number) => operands.slice(-count).map(Number);
  const showText = (text: string) => {
    const matrix = multiply(textMatrix, ctm);
    const point = apply(matrix, 0, 0);
    const size = fontSize * Math.hypot(matrix[2], matrix[3]);
    if (text.trim() !== '') texts.push({ x: point.x, y: point.y, text, ...(size > 0 ? { size } : {}) });
  };
  const moveLine = (tx: number, ty: number) => {
    lineMatrix = multiply([1, 0, 0, 1, tx, ty], lineMatrix);
    textMatrix = lineMatrix;
  };

  for (const token of tokenize(content)) {
    if (token === '[') {
      arrayText = '';
      continue;
    }
    if (token === ']') {
      operands.push(`(${arrayText ?? ''}`);
      arrayText = null;
      continue;
    }
    if (arrayText != null) {
      if (token.startsWith('(')) arrayText += token.slice(1);
      continue;
    }
    if (/^[-+.\d]/.test(token) || token.startsWith('(') || token.startsWith('/')) {
      operands.push(token);
      continue;
    }

    switch (token) {
      case 'q':
        stack.push(ctm);
        break;
      case 'Q':
        ctm = stack.pop() ?? IDENTITY;
        break;
      case 'cm':
        ctm = multiply(numbers(6) as unknown as Matrix, ctm);
        break;
      case 'm': {
        const [x, y] = numbers(2);
        current.push([apply(ctm, x, y)]);
        break;
      }
      case 'l': {
        const [x, y] = numbers(2);
        current.at(-1)?.push(apply(ctm, x, y));
        break;
      }
      case 'c':
      case 'v':
      case 'y': {
        const [x, y] = numbers(2);
        current.at(-1)?.push(apply(ctm, x, y));
        break;
      }
      case 're': {
        const [x, y, w, h] = numbers(4);
        current.push([apply(ctm, x, y), apply(ctm, x + w, y), apply(ctm, x + w, y + h), apply(ctm, x, y + h)]);
        break;
      }
      case 'h': {
        const path = current.at(-1);
        if (path != null && path.length > 0) path.push(path[0]);
        break;
      }
      case 'S':
      case 's':
      case 'f':
      case 'F':
      case 'f*':
      case 'B':
      case 'B*':
      case 'b':
      case 'b*':
        paths.push(...current.filter((path) => path.length >= 2));
        current = [];
        break;
      case 'n':
        current = [];
        break;
      case 'BT':
        textMatrix = IDENTITY;
        lineMatrix = IDENTITY;
        break;
      case 'Tm':
        textMatrix = numbers(6) as unknown as Matrix;
        lineMatrix = textMatrix;
        break;
      case 'Td': {
        const [tx, ty] = numbers(2);
        moveLine(tx, ty);
        break;
      }
      case 'TD': {
        const [tx, ty] = numbers(2);
        leading = -ty;
        moveLine(tx, ty);
        break;
      }
      case 'Tf':
        fontSize = numbers(1)[0];
        break;
      case 'TL':
        leading = numbers(1)[0];
        break;
      case 'T*':
        moveLine(0, -leading);
        break;
      case 'Tj':
      case 'TJ':
        showText(operands.at(-1)?.slice(1) ?? '');
        break;
      case "'":
        moveLine(0, -leading);
        showText(operands.at(-1)?.slice(1) ?? '');
        break;
    }
    operands = [];
  }
  return { paths, texts };
}

/** Inflates a FlateDecode stream with the browser's own decompressor. */
async function inflateWithBrowser(data: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([data as BlobPart]).stream().pipeThrough(new DecompressionStream('deflate'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/**
 * Reads every page content stream in a PDF. Fonts, images, and other streams are skipped by their dictionaries.
 */
export async function readPdfPages(
  bytes: Uint8Array,
  inflate: (data: Uint8Array) => Promise<Uint8Array> = inflateWithBrowser,
): Promise<DrawingPage[]> {
  const text = latin1.decode(bytes);
  const pages: DrawingPage[] = [];
  const streamPattern = /stream\r?\n/g;

  for (let match = streamPattern.exec(text); match != null; match = streamPattern.exec(text)) {
    const dictionaryStart = text.lastIndexOf('<<', match.index);
    const dictionary = dictionaryStart < 0 ? '' : text.slice(dictionaryStart, match.index);
    if (/\/(Subtype|Length1|Length2|Length3|Type\s*\/(XRef|ObjStm|Metadata))\b/.test(dictionary)) continue;

    const start = match.index + match[0].length;
    const end = text.indexOf('endstream', start);
    if (end < 0) break;
    // The line break before "endstream" isn't part of the data, and the browser's decompressor rejects it.
    let dataEnd = end;
    if (bytes[dataEnd - 1] === 0x0a) dataEnd--;
    if (bytes[dataEnd - 1] === 0x0d) dataEnd--;
    const raw = bytes.subarray(start, dataEnd);

    let content: string;
    try {
      content = /\/FlateDecode/.test(dictionary) ? latin1.decode(await inflate(raw)) : latin1.decode(raw);
    } catch {
      continue;
    }
    pages.push(readContentStream(content));
  }
  return pages;
}

/**
 * The widths a member's outline can be, in inches: lumber on edge (2x4 through 2x12) or lying flat. Plates are drawn
 * as outlines too, but at their own sizes (2, 3, 4, 5 inches and so on), so this keeps them out.
 */
const BOARD_WIDTHS = [1.5, 3.5, 5.5, 7.25, 9.25, 11.25];
const BOARD_WIDTH_TOLERANCE = 0.2;

/** Fits a member to a drawn outline: its centerline along the outline's longest side, and its width across. */
function measureOutline(points: readonly Point[]): MeasuredMember | null {
  const distinct = points.filter(
    (point, index) => index === 0 || Math.hypot(point.x - points[index - 1].x, point.y - points[index - 1].y) > 1e-6,
  );
  if (distinct.length < 3) return null;

  let longest = { dx: 0, dy: 0, length: 0 };
  for (let index = 1; index < distinct.length; index++) {
    const dx = distinct[index].x - distinct[index - 1].x;
    const dy = distinct[index].y - distinct[index - 1].y;
    const length = Math.hypot(dx, dy);
    if (length > longest.length) longest = { dx, dy, length };
  }
  if (longest.length === 0) return null;

  const ux = longest.dx / longest.length;
  const uy = longest.dy / longest.length;
  const along = distinct.map((point) => point.x * ux + point.y * uy);
  const across = distinct.map((point) => -point.x * uy + point.y * ux);
  const minAlong = Math.min(...along);
  const maxAlong = Math.max(...along);
  const middle = (Math.min(...across) + Math.max(...across)) / 2;
  const width = Math.max(...across) - Math.min(...across);
  const toPoint = (a: number): Point => ({ x: a * ux - middle * uy, y: a * uy + middle * ux });

  return { from: toPoint(minAlong), to: toPoint(maxAlong), width };
}

/** Keeps the largest group of members that touch, which is the truss rather than boxes in the drawing's tables. */
function largestTouchingGroup(members: readonly { member: MeasuredMember; outline: readonly Point[] }[], gap: number) {
  const boxes = members.map(({ outline }) => ({
    minX: Math.min(...outline.map((point) => point.x)) - gap,
    maxX: Math.max(...outline.map((point) => point.x)) + gap,
    minY: Math.min(...outline.map((point) => point.y)) - gap,
    maxY: Math.max(...outline.map((point) => point.y)) + gap,
  }));
  const group = members.map((_, index) => index);
  const find = (index: number): number => (group[index] === index ? index : (group[index] = find(group[index])));
  boxes.forEach((box, i) =>
    boxes.slice(i + 1).forEach((other, offset) => {
      if (box.minX <= other.maxX && other.minX <= box.maxX && box.minY <= other.maxY && other.minY <= box.maxY)
        group[find(i)] = find(i + 1 + offset);
    }),
  );

  const sizes = new Map<number, number>();
  members.forEach((_, index) => sizes.set(find(index), (sizes.get(find(index)) ?? 0) + 1));
  const [largest] = [...sizes.entries()].sort((a, b) => b[1] - a[1])[0] ?? [];
  return members.filter((_, index) => find(index) === largest);
}

/**
 * Measures the truss on a drawing page, in inches with x from the truss's left end and y up from the underside of its
 * bottom chord, or `null` if the page has no stated scale or no members.
 */
export function measureDrawing(page: DrawingPage): MeasuredDrawing | null {
  const scaleText = page.texts.map(({ text }) => /Scale\s*=\s*1\s*:\s*([\d.]+)/i.exec(text)).find((m) => m != null);
  if (scaleText == null) return null;
  const scale = Number(scaleText[1]);
  if (!(scale > 0)) return null;
  // PDF units are points, 1/72 inch on paper.
  const inchesPerPoint = scale / 72;

  const candidates = page.paths.flatMap((outline) => {
    const member = measureOutline(outline);
    if (member == null) return [];
    const width = member.width * inchesPerPoint;
    const length = Math.hypot(member.to.x - member.from.x, member.to.y - member.from.y) * inchesPerPoint;
    return BOARD_WIDTHS.some((board) => Math.abs(width - board) <= BOARD_WIDTH_TOLERANCE) && length >= 2 * width
      ? [{ member, outline }]
      : [];
  });
  const truss = largestTouchingGroup(candidates, 1);
  if (truss.length < 3) return null;

  const outlinePoints = truss.flatMap(({ outline }) => outline);
  const minX = Math.min(...outlinePoints.map((point) => point.x));
  const minY = Math.min(...outlinePoints.map((point) => point.y));
  const maxX = Math.max(...outlinePoints.map((point) => point.x));
  const maxY = Math.max(...outlinePoints.map((point) => point.y));
  const toInches = (point: Point): Point => ({
    x: (point.x - minX) * inchesPerPoint,
    y: (point.y - minY) * inchesPerPoint,
  });

  const labels = matchLabels(
    truss.map(({ member }) => member),
    page.texts.filter(({ text }) => MEMBER_LABEL.test(text.trim())),
    MAX_LABEL_DISTANCE / inchesPerPoint,
  );
  const pieces: Piece[] = truss.map(({ member, outline }, index) => {
    const from = toInches(member.from);
    const to = toInches(member.to);
    // Left to right, or bottom to top for verticals, so the list reads the way the drawing does.
    const flip = from.x > to.x + 1 / 16 || (Math.abs(from.x - to.x) <= 1 / 16 && from.y > to.y);
    const xs = outline.map((point) => toInches(point).x);
    return {
      label: labels.get(index),
      from: flip ? to : from,
      to: flip ? from : to,
      width: member.width * inchesPerPoint,
      minX: Math.min(...xs),
      maxX: Math.max(...xs),
    };
  });
  const against = findPiecesAgainstOthers(pieces);
  const chords = traceChords(pieces.filter((_, index) => !against.has(index)));
  // x is from the truss's left end, where the bottom chord starts, so an overhang reaches past 0.
  const leftEnd = chords?.leftEnd ?? 0;
  const shift = (point: Point): Point => ({ x: round(point.x - leftEnd), y: round(point.y) });

  const members = pieces
    .map((piece, index) => ({
      ...(piece.label != null ? { label: piece.label } : {}),
      from: shift(piece.from),
      to: shift(piece.to),
      width: round(piece.width),
      ...(against.has(index) ? { against: against.get(index)! } : {}),
    }))
    .sort((a, b) => a.from.x - b.from.x || a.from.y - b.from.y);

  // Single letters near the truss are its joint labels, placed beside their joints.
  const margin = 30;
  const jointLabels = page.texts
    .filter(
      ({ x, y, text }) =>
        /^[A-Z]{1,2}$/.test(text.trim()) &&
        x >= minX - margin &&
        x <= maxX + margin &&
        y >= minY - margin &&
        y <= maxY + margin,
    )
    .map(({ x, y, text }) => ({ ...shift(toInches({ x, y })), text: text.trim() }));

  const typed = readDimensions(page.texts, { minX, maxX, minY, maxY }, (maxX - minX) * inchesPerPoint);
  const reconciled =
    chords == null || typed == null
      ? null
      : reconcileDimensions(
          typed,
          chords,
          pieces.filter(
            (piece, index) => !against.has(index) && !chords.top.includes(piece) && !chords.bottom.includes(piece),
          ),
        );

  return {
    scale,
    overallLength: round((maxX - minX) * inchesPerPoint),
    height: round((maxY - minY) * inchesPerPoint),
    members,
    jointLabels,
    outline: reconciled?.outline ?? chords?.outline.map((point) => ({ ...shift(point), line: point.line })) ?? null,
    overhangs: chords == null ? null : { left: round(chords.overhangs.left), right: round(chords.overhangs.right) },
    dimensions: reconciled?.dimensions ?? null,
  };
}

/** A typed dimension such as 7-9-15 (feet, inches, sixteenths), with its value in inches. */
interface TypedDimension {
  readonly text: string;
  readonly inches: number;
}

const DIMENSION = /^(-)?(\d+)-(\d+)-(\d+)$/;

function parseDimension(text: string): TypedDimension | null {
  const match = DIMENSION.exec(text.trim());
  if (match == null) return null;
  const inches = Number(match[2]) * 12 + Number(match[3]) + Number(match[4]) / 16;
  return { text: text.trim(), inches: match[1] != null ? -inches : inches };
}

/**
 * How far, in page points, dimensions may sit from the truss. Drawings print their rows at set places on the page,
 * so a low truss leaves a wide gap above it; the rows still have to read as running totals to count.
 */
const ROWS_ABOVE_MARGIN = 200;
const ROWS_BELOW_MARGIN = 80;
const SIDE_MARGIN = 70;
/** How close, in page points, an end elevation's label is to its end of the truss. */
const END_LABEL_MARGIN = 30;

/**
 * Reads the dimensions around the truss. Drawings such as MiTek's give two rows above the truss and two below: running
 * totals from the left end and the panel lengths that add up to them. The running totals are the positions; a row
 * that only gives each end's elevation, or a lone row, is used only when it reads as running totals.
 */
function readDimensions(
  texts: readonly DrawingText[],
  box: { minX: number; maxX: number; minY: number; maxY: number },
  lengthInInches: number,
) {
  const typed = texts.flatMap((text) => {
    const dimension = parseDimension(text.text);
    return dimension == null ? [] : [{ ...dimension, x: text.x, y: text.y }];
  });
  const inTruss = (x: number) => x >= box.minX - SIDE_MARGIN && x <= box.maxX + SIDE_MARGIN;
  const rowsOf = (items: typeof typed) => {
    const rows: (typeof typed)[] = [];
    for (const item of [...items].sort((a, b) => a.x - b.x)) {
      const row = rows.find((candidate) => Math.abs(candidate[0].y - item.y) <= 1);
      if (row != null) row.push(item);
      else rows.push([item]);
    }
    return rows;
  };
  // Two labels, one at each end of the truss: the elevation of the truss's bottom there, not a position along it.
  // Elevations stay well under the truss's length, which a row of positions reaches.
  const isEndElevations = (row: typeof typed) =>
    row.length === 2 &&
    Math.max(row[0].inches, row[1].inches) < lengthInInches / 2 &&
    Math.abs(row[0].x - box.minX) <= END_LABEL_MARGIN &&
    Math.abs(row[1].x - box.maxX) <= END_LABEL_MARGIN;
  // The row of running totals: the panel lengths of another row add up to it, or it climbs from the left end on its own.
  // Either way it runs most of the truss's length.
  const runningTotals = (allRows: (typeof typed)[]) => {
    const rows = allRows.filter((row) => !isEndElevations(row) && row.at(-1)!.inches >= lengthInInches / 2);
    const paired = rows.find((row) =>
      rows.some(
        (other) =>
          other !== row &&
          other.length === row.length &&
          other.every(
            (_, index) =>
              Math.abs(other.slice(0, index + 1).reduce((sum, item) => sum + item.inches, 0) - row[index].inches) <
              1 / 8,
          ),
      ),
    );
    if (paired != null) return paired;
    const climbing = rows
      .filter(
        (row) => row.length >= 2 && row.every((item, index) => index === 0 || item.inches > row[index - 1].inches),
      )
      .sort((a, b) => b.length - a.length);
    return climbing[0] ?? [];
  };

  const above = typed.filter((item) => item.y > box.maxY && item.y <= box.maxY + ROWS_ABOVE_MARGIN && inTruss(item.x));
  const below = typed.filter((item) => item.y < box.minY && item.y >= box.minY - ROWS_BELOW_MARGIN && inTruss(item.x));
  const beside = (side: 'left' | 'right') =>
    typed.filter(
      (item) =>
        item.y >= box.minY - 1 &&
        item.y <= box.maxY + 1 &&
        (side === 'left'
          ? item.x < box.minX && item.x >= box.minX - SIDE_MARGIN
          : item.x > box.maxX && item.x <= box.maxX + SIDE_MARGIN),
    );

  const aboveRows = rowsOf(above);
  const belowRows = rowsOf(below);
  const top = runningTotals(aboveRows).filter((item) => item.inches !== 0);
  const bottom = runningTotals(belowRows).filter((item) => item.inches !== 0);
  const heights = { left: beside('left'), right: beside('right') };
  const endElevations = [...aboveRows, ...belowRows].find(isEndElevations) ?? [];
  if (top.length === 0 && bottom.length === 0 && heights.left.length === 0 && heights.right.length === 0) return null;
  return { top, bottom, heights, endElevations };
}

/** How far, in inches, measured lines can be off from the exact dimensions; within this, the dimension is used. */
const SNAP_TOLERANCE = 0.375;
/** Beyond the snap tolerance and within this, a dimension and a line are likely the same thing, misdrawn or misread. */
const DISAGREEMENT_RANGE = 3;
/** How far a web's end may be from a chord dimension, in inches, and still be what it marks: webs meet inside joints. */
const WEB_END_RANGE = 4;

function formatInches(value: number) {
  return `${round(value)}`;
}

/**
 * Matches the typed dimensions to the measured outline and webs: corners within a line's error of a dimension take
 * its exact value, and anything that's further off, or that nothing is drawn at, is reported rather than guessed.
 */
function reconcileDimensions(
  typed: NonNullable<ReturnType<typeof readDimensions>>,
  chords: NonNullable<ReturnType<typeof traceChords>>,
  webs: readonly Piece[],
) {
  const { leftEnd } = chords;
  const disagreements: string[] = [];
  const length = chords.rightEnd - leftEnd;
  // Positions past the ends are where an overhang ends, not panel points.
  const alongTruss = (dimension: TypedDimension) =>
    dimension.inches > SNAP_TOLERANCE && dimension.inches < length + SNAP_TOLERANCE + DISAGREEMENT_RANGE;
  const overhangEnds = typed.top.filter((dimension) => !alongTruss(dimension));
  // The ends and the underside are 0 whether or not the drawing says so; they're only snapped to, never reported.
  const zero = { text: '0-0-0', inches: 0 };
  const xDimensions = [zero, ...typed.top.filter(alongTruss), ...typed.bottom.filter(alongTruss)];
  const yDimensions = [zero, ...typed.heights.left, ...typed.heights.right, ...typed.endElevations];
  const nearest = (value: number, dimensions: readonly TypedDimension[]) =>
    dimensions.reduce<TypedDimension | null>(
      (best, dimension) =>
        best == null || Math.abs(dimension.inches - value) < Math.abs(best.inches - value) ? dimension : best,
      null,
    );

  const outline = chords.outline.map((point) => {
    const measured = { x: point.x - leftEnd, y: point.y };
    const snapped = { ...measured };
    const from: string[] = [];
    for (const axis of ['x', 'y'] as const) {
      const dimension = nearest(measured[axis], axis === 'x' ? xDimensions : yDimensions);
      if (dimension == null) continue;
      const off = Math.abs(dimension.inches - measured[axis]);
      if (off <= SNAP_TOLERANCE) {
        snapped[axis] = dimension.inches;
        if (dimension.text !== '0-0-0') from.push(dimension.text);
      } else if (off <= DISAGREEMENT_RANGE && dimension !== zero)
        disagreements.push(
          `The outline corner measured at (${formatInches(measured.x)}, ${formatInches(measured.y)}) is ` +
            `${formatInches(off)} from the ${axis === 'x' ? 'position' : 'height'} ${dimension.text} ` +
            `(${formatInches(dimension.inches)}); the nearest dimension doesn't match it.`,
        );
    }
    return {
      x: round(snapped.x),
      y: round(snapped.y),
      line: point.line,
      ...(from.length > 0 ? { from: from.join(', ') } : {}),
    };
  });

  const describeAlong = (dimensions: readonly TypedDimension[], face: readonly Piece[], side: 1 | -1) =>
    dimensions.map((dimension): ChordDimension => {
      const x = dimension.inches + leftEnd;
      const chord = face.find((piece) => x >= piece.minX - 1 && x <= piece.maxX + 1);
      const chordY = chord == null ? null : faceAt(chord, side, x);
      const web =
        chordY == null
          ? null
          : webs.find((piece) =>
              [piece.from, piece.to].some(
                (end) => Math.abs(end.x - x) <= WEB_END_RANGE && Math.abs(end.y - chordY) <= WEB_END_RANGE * 2,
              ),
            );
      // A corner of this chord, not one above or below it on the other chord.
      const drawn = outline.some(
        (corner) =>
          chordY != null && Math.abs(corner.x - dimension.inches) <= SNAP_TOLERANCE && Math.abs(corner.y - chordY) <= 1,
      )
        ? 'a corner'
        : web != null
          ? (web.label ?? 'a web')
          : null;
      return { text: dimension.text, x: round(dimension.inches), drawn };
    });
  const topChord = describeAlong(typed.top.filter(alongTruss), chords.top, 1);
  const bottomChord = describeAlong(typed.bottom.filter(alongTruss), chords.bottom, -1);
  for (const dimension of overhangEnds) {
    const side = dimension.inches <= 0 ? 'left' : 'right';
    const typedOverhang = side === 'left' ? -dimension.inches : dimension.inches - length;
    const measured = chords.overhangs[side];
    if (Math.abs(typedOverhang - measured) > SNAP_TOLERANCE)
      disagreements.push(
        `The top chord dimension ${dimension.text} makes a ${formatInches(typedOverhang)} ${side} overhang, but the ` +
          `lines measure ${formatInches(measured)}.`,
      );
  }
  for (const [name, dimensions] of [
    ['top', topChord],
    ['bottom', bottomChord],
  ] as const)
    for (const dimension of dimensions.filter((candidate) => candidate.drawn == null))
      disagreements.push(
        `The ${name} chord dimension ${dimension.text} (${dimension.x}) has no web or corner drawn at it; it may ` +
          'mark a splice, a bearing, a brace, or a load rather than a panel point.',
      );

  const typedLength = Math.max(
    ...[...typed.top, ...typed.bottom].filter(alongTruss).map((dimension) => dimension.inches),
    0,
  );
  if (typedLength > 0 && Math.abs(typedLength - length) > SNAP_TOLERANCE)
    disagreements.push(
      `The dimensions run to ${formatInches(typedLength)}, but the lines measure ${formatInches(length)} from end ` +
        'to end.',
    );

  return {
    outline,
    dimensions: {
      topChord,
      bottomChord,
      heights: {
        left: typed.heights.left.map((dimension) => dimension.text),
        right: typed.heights.right.map((dimension) => dimension.text),
      },
      disagreements,
    },
  };
}

/** A measured member in inches, with how far its drawn outline reaches left and right. */
interface Piece {
  readonly label?: string;
  readonly from: Point;
  readonly to: Point;
  readonly width: number;
  readonly minX: number;
  readonly maxX: number;
}

const lengthOf = ({ from, to }: Piece) => Math.hypot(to.x - from.x, to.y - from.y);
const angleOf = ({ from, to }: Piece) => Math.atan2(to.y - from.y, to.x - from.x);
const PARALLEL_RADIANS = (1.5 * Math.PI) / 180;
/** How far, in inches, two faces may be apart and still be touching, since lines are measured to about 1/8 inch. */
const FACE_GAP = 0.375;

/**
 * Finds members lying face to face along a longer one, such as a heel slider along the top chord: they aren't webs, and
 * they don't shape the outline.
 */
function findPiecesAgainstOthers(pieces: readonly Piece[]) {
  const against = new Map<number, string>();
  pieces.forEach((piece, index) => {
    const middle = { x: (piece.from.x + piece.to.x) / 2, y: (piece.from.y + piece.to.y) / 2 };
    const other = pieces.find((candidate) => {
      if (candidate === piece || lengthOf(candidate) <= lengthOf(piece)) return false;
      const turn = Math.abs(angleOf(candidate) - angleOf(piece)) % Math.PI;
      if (Math.min(turn, Math.PI - turn) > PARALLEL_RADIANS) return false;
      const length = lengthOf(candidate);
      const ux = (candidate.to.x - candidate.from.x) / length;
      const uy = (candidate.to.y - candidate.from.y) / length;
      const along = (middle.x - candidate.from.x) * ux + (middle.y - candidate.from.y) * uy;
      const across = Math.abs(-(middle.x - candidate.from.x) * uy + (middle.y - candidate.from.y) * ux);
      return along > 0 && along < length && Math.abs(across - (candidate.width + piece.width) / 2) <= FACE_GAP;
    });
    if (other != null) against.set(index, other.label ?? 'a chord');
  });
  return against;
}

/** Steeper than this, a member is a vertical or a web, never part of the top or bottom of the outline. */
const STEEPEST_CHORD_RADIANS = (80 * Math.PI) / 180;

/** The y of a member's upper (side 1) or lower (side -1) face at x, along its line. */
function faceAt(piece: Piece, side: 1 | -1, x: number) {
  const slope = (piece.to.y - piece.from.y) / (piece.to.x - piece.from.x);
  return piece.from.y + (x - piece.from.x) * slope + side * (piece.width / 2) * Math.hypot(1, slope);
}

/**
 * Traces the outline along the chords' outer faces: the members whose upper face is highest where they run are the
 * top chord, and those whose lower face is lowest are the bottom chord. Corners are where neighboring faces meet at a
 * change of slope, and the ends are where the bottom chord ends.
 */
function traceChords(pieces: readonly Piece[]) {
  const sloped = pieces.filter((piece) => {
    const angle = Math.abs(angleOf(piece)) % Math.PI;
    return Math.min(angle, Math.PI - angle) < STEEPEST_CHORD_RADIANS;
  });
  const onFace = (side: 1 | -1) =>
    sloped
      .filter((piece) => {
        const x = (piece.minX + piece.maxX) / 2;
        const y = faceAt(piece, side, x);
        return sloped.every(
          (other) => other === piece || x < other.minX || x > other.maxX || side * (faceAt(other, side, x) - y) <= 0.25,
        );
      })
      .sort((a, b) => a.minX + a.maxX - (b.minX + b.maxX));
  const top = onFace(1);
  const bottom = onFace(-1);
  if (top.length === 0 || bottom.length === 0) return null;

  const leftEnd = Math.min(...bottom.map((piece) => piece.minX));
  const rightEnd = Math.max(...bottom.map((piece) => piece.maxX));
  const corners = (face: readonly Piece[], side: 1 | -1) =>
    face.slice(1).flatMap((piece, index) => {
      const previous = face[index];
      const s1 = (previous.to.y - previous.from.y) / (previous.to.x - previous.from.x);
      const s2 = (piece.to.y - piece.from.y) / (piece.to.x - piece.from.x);
      // Pieces in line are a splice, not a corner.
      if (Math.abs(Math.atan(s1) - Math.atan(s2)) < PARALLEL_RADIANS) return [];
      const y1 = faceAt(previous, side, 0);
      const y2 = faceAt(piece, side, 0);
      const x = (y2 - y1) / (s1 - s2);
      return [{ x, y: faceAt(piece, side, x) }];
    });

  // Each end is a heel, or a point where the chords meet.
  const end = (x: number, side: 'Left' | 'Right') => {
    const topY = faceAt(side === 'Left' ? top[0] : top.at(-1)!, 1, x);
    const bottomY = faceAt(side === 'Left' ? bottom[0] : bottom.at(-1)!, -1, x);
    return topY - bottomY < 0.25
      ? [{ x, y: bottomY }]
      : side === 'Left'
        ? [
            { x, y: bottomY },
            { x, y: topY },
          ]
        : [
            { x, y: topY },
            { x, y: bottomY },
          ];
  };
  const left = end(leftEnd, 'Left');
  const right = end(rightEnd, 'Right');
  const outline: (Point & { line: MeasuredOutlinePoint['line'] })[] = [
    ...left.map((point, index) => ({
      ...point,
      line: index < left.length - 1 ? ('Heel' as const) : ('TopChord' as const),
    })),
    ...corners(top, 1).map((point) => ({ ...point, line: 'TopChord' as const })),
    ...right.map((point, index) => ({
      ...point,
      line: index < right.length - 1 ? ('Heel' as const) : ('BottomChord' as const),
    })),
    ...corners(bottom, -1)
      .reverse()
      .map((point) => ({ ...point, line: 'BottomChord' as const })),
  ];

  return {
    leftEnd,
    rightEnd,
    top,
    bottom,
    outline,
    overhangs: {
      left: Math.max(0, leftEnd - Math.min(...top.map((piece) => piece.minX))),
      right: Math.max(0, Math.max(...top.map((piece) => piece.maxX)) - rightEnd),
    },
  };
}

/** Member names such as T1, B2, W10, or EV1. */
const MEMBER_LABEL = /^[A-Z]{1,2}\d+$/;

/** How far, in inches, a label's middle may be from a member's centerline and still name it. */
const MAX_LABEL_DISTANCE = 4;

function distanceToSegment(point: Point, from: Point, to: Point) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const lengthSquared = dx * dx + dy * dy;
  const t =
    lengthSquared === 0
      ? 0
      : Math.max(0, Math.min(1, ((point.x - from.x) * dx + (point.y - from.y) * dy) / lengthSquared));
  return Math.hypot(point.x - (from.x + t * dx), point.y - (from.y + t * dy));
}

/**
 * Names members by the labels drawn on them. Labels are placed by their baseline's left end, so their middle is
 * estimated from their size; each label names one member and each member gets one label, closest pairs first.
 */
function matchLabels(members: readonly MeasuredMember[], labels: readonly DrawingText[], maxDistance: number) {
  const pairs = members.flatMap((member, memberIndex) =>
    labels.flatMap(({ x, y, text, size = 0 }, labelIndex) => {
      const middle = { x: x + 0.3 * size * text.trim().length, y: y + 0.35 * size };
      const distance = distanceToSegment(middle, member.from, member.to);
      return distance <= maxDistance ? [{ memberIndex, labelIndex, distance }] : [];
    }),
  );

  const named = new Map<number, string>();
  const used = new Set<number>();
  for (const { memberIndex, labelIndex } of pairs.sort((a, b) => a.distance - b.distance)) {
    if (named.has(memberIndex) || used.has(labelIndex)) continue;
    named.set(memberIndex, labels[labelIndex].text.trim());
    used.add(labelIndex);
  }
  return named;
}

function round(value: number) {
  return Math.round(value * 16) / 16;
}

function describeDirection({ from, to }: MeasuredMember) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  if (Math.abs(dy) <= 1 / 8) return 'horizontal';
  if (Math.abs(dx) <= 1 / 8) return 'vertical';
  return `${dy > 0 ? 'rising' : 'falling'} ${Math.round((Math.abs(dy) / Math.abs(dx)) * 12 * 100) / 100}/12`;
}

function describeDimensions(dimensions: DrawingDimensions) {
  const along = (name: string, list: readonly ChordDimension[]) =>
    list.length === 0
      ? []
      : [
          `${name} dimensions, from the left end, with what’s drawn at each: ` +
            list.map(({ text, x, drawn }) => `${text} = ${x} (${drawn ?? 'nothing drawn there'})`).join(', ') +
            '.',
        ];
  const heights = (['left', 'right'] as const).flatMap((side) =>
    dimensions.heights[side].length > 0 ? [`${side} ${dimensions.heights[side].join(', ')}`] : [],
  );
  return [
    ...along('Top chord', dimensions.topChord),
    ...along('Bottom chord', dimensions.bottomChord),
    ...(heights.length > 0 ? [`Heights beside the ends: ${heights.join('; ')}.`] : []),
    ...(dimensions.disagreements.length > 0
      ? [
          'Where the dimensions and the lines disagree; don’t pick one silently. Say which you used and why, or ask ' +
            'the designer:',
          ...dimensions.disagreements.map((disagreement) => `- ${disagreement}`),
        ]
      : []),
  ];
}

/** The measurements as the model reads them, alongside the drawing itself. */
export function describeMeasuredDrawing(name: string, drawing: MeasuredDrawing) {
  const point = ({ x, y }: Point) => `(${x}, ${y})`;
  return [
    `Measured from the vector lines of ${name} at its 1:${drawing.scale} scale, in inches: x from the truss’s left ` +
      'end, y up from the underside of the bottom chord. Each member is its centerline end to end with its width; ' +
      'its faces are half the width to either side, and the drawing’s name for it when a label sits on it. The ' +
      'names only say whether a piece is a chord or a web (a chord piece stays a chord even when it’s vertical); ' +
      'Paragon names members its own way. These are measured, so use them for the geometry over reading ' +
      'the picture. They’re off by up to about 1/8 inch; the drawing’s dimensions are exact.',
    `Overall: ${drawing.overallLength} long, ${drawing.height} high.`,
    ...(drawing.outline != null
      ? [
          'Outline traced along the chords’ outer faces, as create_truss takes it, with x from where the bottom ' +
            'chord starts. Each coordinate within 3/8 inch of a dimension has been set to it exactly, and `from` ' +
            'names those dimensions; coordinates without one are measured: ' +
            JSON.stringify(
              drawing.outline.map(({ x, y, line, from }) => ({ x, y, line, ...(from != null ? { from } : {}) })),
            ) +
            '.',
          `Overhangs past the ends: left ${drawing.overhangs!.left}, right ${drawing.overhangs!.right}.`,
        ]
      : []),
    ...(drawing.dimensions != null ? describeDimensions(drawing.dimensions) : []),
    'Members:',
    ...drawing.members.map(
      (member) =>
        `- ${member.label != null ? `${member.label}: ` : ''}${point(member.from)} to ${point(member.to)}, ` +
        `${member.width} wide, ${describeDirection(member)}` +
        (member.against != null
          ? `, lying face to face along ${member.against}: a slider (at a heel), scab, or stacked piece, not a web`
          : ''),
    ),
    'A web’s centerline isn’t its panel point: a vertical beside a corner or end sits half its width inside it, so ' +
      'take panel points from the dimensions.',
    drawing.jointLabels.length > 0
      ? `Joint letters, where their labels sit beside the joints: ${drawing.jointLabels
          .map((label) => `${label.text} ${point(label)}`)
          .join(', ')}.`
      : '',
  ]
    .filter((line) => line !== '')
    .join('\n');
}

/**
 * Measures the truss in a PDF truss design drawing and reads its loading, or returns `null` if the PDF has neither,
 * such as a scanned image.
 */
export async function measurePdfDrawing(
  name: string,
  bytes: Uint8Array,
  inflate?: (data: Uint8Array) => Promise<Uint8Array>,
): Promise<string | null> {
  try {
    for (const page of await readPdfPages(bytes, inflate)) {
      const drawing = measureDrawing(page);
      const loads = readDrawingLoads(page.texts);
      if (drawing == null && loads == null) continue;
      return [
        ...(drawing != null ? [describeMeasuredDrawing(name, drawing)] : []),
        ...(loads != null ? [describeDrawingLoads(loads)] : []),
      ].join('\n\n');
    }
  } catch {
    // An unreadable PDF still goes to the model as a picture.
  }
  return null;
}
